/**
 * Acesso ao acervo pelo manifesto.
 *
 * Nenhum caminho de arquivo é escrito à mão em lugar nenhum do jogo: tudo passa
 * por aqui. É essa indireção que torna trocar, atualizar ou reprocessar um
 * asset uma operação mecânica, e não uma caçada por strings pelo código.
 */

export const ASSETS_BASE = '/assets';

export interface SpriteGrid {
  readonly frameWidth: number;
  readonly frameHeight: number;
  readonly columns: number;
  readonly rows: number;
  readonly charactersPerSheet?: number;
  readonly framesPerCharacter?: number;
  readonly noShadow?: boolean;
}

export interface ImageEntry {
  readonly file: string;
  readonly width: number;
  readonly height: number;
  readonly bytes: number;
  readonly sourceBytes: number;
  readonly role: string;
  readonly grid?: SpriteGrid;
}

export interface AudioEntry {
  readonly ogg: string;
  readonly m4a: string | null;
  readonly bytes: number;
  readonly role: string;
}

export interface ContentEntry {
  readonly file: string;
  readonly count: number;
}

export interface Manifest {
  readonly manifestVersion: number;
  readonly generatedAt: string;
  readonly source: string;
  readonly excluded: readonly string[];
  readonly content: Readonly<Record<string, ContentEntry>>;
  readonly images: Readonly<Record<string, Readonly<Record<string, ImageEntry>>>>;
  readonly audio: Readonly<Record<string, Readonly<Record<string, AudioEntry>>>>;
  readonly fx: {
    readonly effects?: Readonly<Record<string, { file: string; bytes: number }>>;
    readonly runtime?: Readonly<Record<string, number>>;
  };
  readonly fonts: Readonly<Record<string, { file: string; bytes: number }>>;
}

let cached: Promise<Manifest> | null = null;

export function loadManifest(): Promise<Manifest> {
  cached ??= fetch(`${ASSETS_BASE}/manifest.json`).then((r) => {
    if (!r.ok) throw new Error(`manifesto não carregou: HTTP ${r.status}`);
    return r.json() as Promise<Manifest>;
  });
  return cached;
}

export function imageUrl(manifest: Manifest, group: string, stem: string): string {
  const entry = manifest.images[group]?.[stem];
  if (!entry) throw new Error(`imagem desconhecida: ${group}/${stem}`);
  return `${ASSETS_BASE}/img/${group}/${entry.file}`;
}

/** As duas fontes de um som, na ordem em que o navegador deve tentar. */
export function audioSources(
  manifest: Manifest,
  group: string,
  stem: string,
): ReadonlyArray<{ src: string; type: string }> {
  const entry = manifest.audio[group]?.[stem];
  if (!entry) throw new Error(`som desconhecido: ${group}/${stem}`);

  const sources = [{ src: `${ASSETS_BASE}/audio/${group}/${entry.ogg}`, type: 'audio/ogg' }];
  if (entry.m4a) {
    // O Safari não toca Vorbis. Sem esta alternativa, o jogo fica mudo lá —
    // e silêncio parece defeito, não escolha.
    sources.push({ src: `${ASSETS_BASE}/audio/${group}/${entry.m4a}`, type: 'audio/mp4' });
  }
  return sources;
}

export function effectUrl(manifest: Manifest, name: string): string {
  const entry = manifest.fx.effects?.[name];
  if (!entry) throw new Error(`efeito desconhecido: ${name}`);
  return `${ASSETS_BASE}/fx/${entry.file}`;
}

/**
 * Onde fica o quadro `index` dentro da folha.
 *
 * Folhas de personagem guardam 8 personagens de 12 quadros cada (3 colunas de
 * andar × 4 direções). O índice global percorre personagem a personagem, que é
 * como o acervo os numera.
 */
export function frameRect(
  grid: SpriteGrid,
  index: number,
): { x: number; y: number; width: number; height: number } {
  const column = index % grid.columns;
  const row = Math.floor(index / grid.columns);
  return {
    x: column * grid.frameWidth,
    y: row * grid.frameHeight,
    width: grid.frameWidth,
    height: grid.frameHeight,
  };
}

/**
 * Quadros de uma direção de caminhada, para um personagem de uma folha.
 *
 * Direções na ordem do acervo: 0 baixo, 1 esquerda, 2 direita, 3 cima.
 * Os três quadros da linha são pé esquerdo, parado, pé direito — e a animação
 * de caminhar percorre 0,1,2,1, que é o vaivém que o olho espera.
 */
export function walkFrames(grid: SpriteGrid, character: number, direction: number): number[] {
  const perSheet = grid.charactersPerSheet ?? 1;
  const charColumn = perSheet === 1 ? 0 : character % 4;
  const charRow = perSheet === 1 ? 0 : Math.floor(character / 4);

  const baseColumn = charColumn * 3;
  const baseRow = charRow * 4 + direction;

  const at = (step: number): number => (baseRow * grid.columns) + baseColumn + step;
  return [at(0), at(1), at(2), at(1)];
}

export function formatBytes(n: number): string {
  const units = ['B', 'KB', 'MB'];
  let value = n;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(value < 10 && unit > 0 ? 1 : 0)} ${units[unit]}`;
}

/**
 * O desenho de combate de uma criatura, e de que tamanho ele é.
 *
 * Duas galerias, nesta ordem: a de combate lateral primeiro, porque a figura é
 * menor e está desenhada de perfil — que é como a unidade se apresenta num
 * tabuleiro visto de cima. A de frente é a reserva, para o monstro que só
 * existe lá.
 *
 * Fica aqui, e não no tabuleiro, porque três lugares precisam da MESMA
 * resposta: o tabuleiro que desenha, a escaramuça que decide quantas casas o
 * bicho ocupa, e a cena de combate que o põe no palco. Escolhas diferentes
 * dariam um monstro de duas casas desenhado com a arte de outra galeria.
 */
export function battlerArt(
  manifest: Manifest,
  battler: string,
): { group: 'sv_enemies' | 'enemies'; entry: ImageEntry; url: string } | null {
  if (!battler) return null;

  for (const group of ['sv_enemies', 'enemies'] as const) {
    const entry = manifest.images[group]?.[battler];
    if (entry) {
      return { group, entry, url: `${ASSETS_BASE}/img/${group}/${entry.file}` };
    }
  }
  return null;
}
